import React, { useState } from 'react'
import PropTypes from 'prop-types'

import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { Menu, MenuItem } from '@mui/material'

import { get } from 'utils/lodash'
import { handleError } from 'utils/error'
import { getCustomerDetails } from 'middleware/actions/customers'

import { ERROR_EXCEPTIONS } from 'settings/error'

import T from 'T'
import Loader from 'components/common/loader'
import HHSwitchIconField from 'components/form-fields/v5/HHSwitchIconField'
import HHConfirmDialog from 'components/common/HHConfirmDialog'
import { useDeleteBeneficialOwnerContactMutation, usePatchBeneficialOwnerContactMutation } from 'api/contact/beneficialOwnersCrud'

const { DATA_INTEGRITY_EXCEPTION } = ERROR_EXCEPTIONS

const BeneficialOwnersActionMenu = ({ anchorEl, accountId, selectedContact = {}, onEdit, onClose }) => {
  const dispatch = useDispatch()
  const [isOpenConfirmDeleteDialog, setIsOpenConfirmDeleteDialog] = useState(false)
  const [failureDialog, setFailureDialog] = useState({ isOpen: false, message: '' })
  const [patchBeneficialOwner, { isLoading: isPatchBeneficialOwnerLoading }] = usePatchBeneficialOwnerContactMutation()
  const [deleteBeneficialOwner, { isLoading: isDeleteContactLoading }] = useDeleteBeneficialOwnerContactMutation()
  const isActive = get(selectedContact, 'active')
  const isPropertyOwner = get(selectedContact, 'propertyOwner')
  const isBeneficialOwnerOverride = get(selectedContact, 'beneficialOwnerOverride')
  const contactId = get(selectedContact, 'id')
  const switchMenuSx = {
    '.MuiFormLabel-root': { color: 'text.primary' },
    '.MuiFormControlLabel-root': { width: '100%', display: 'flex', justifyContent: 'space-between' },
  }
  const handleContactActiveStatus = isChecked => {
    const payload = { accountId, id: contactId, active: isChecked }
    patchBeneficialOwner(payload)
      .unwrap()
      .then(() => {
        dispatch(getCustomerDetails({ accountId }))
        toast.success(isChecked ? T.CONTACT_ACTIVATE_SUCCESS : T.CONTACT_DEACTIVATE_SUCCESS)
      })
      .catch(handleError)
      .finally(onClose)
  }

  const handlePropertyOwnerStatus = isChecked => {
    const payload = { accountId, id: contactId, propertyOwner: isChecked }
    patchBeneficialOwner(payload)
      .unwrap()
      .then(() => {
        dispatch(getCustomerDetails({ accountId }))
        toast.success(T.CONTACT_UPDATED_SUCCESS)
      })
      .catch(handleError)
      .finally(onClose)
  }

  const handleBeneficialOwnerStatus = isChecked => {
    const payload = { accountId, id: contactId, beneficialOwnerOverride: isChecked }
    patchBeneficialOwner(payload)
      .unwrap()
      .then(() => {
        dispatch(getCustomerDetails({ accountId }))
        toast.success(T.CONTACT_UPDATED_SUCCESS)
      })
      .catch(handleError)
      .finally(onClose)
  }

  const handleDeleteConfirm = () => {
    deleteBeneficialOwner({ accountId, id: contactId })
      .unwrap()
      .then(() => {
        dispatch(getCustomerDetails({ accountId }))
        toast.success(T.DELETE_CONTACT_SUCCESS_MSG)
      })
      .catch(error => {
        const isDataIntegrityIssue = get(error, 'data.exceptionType') === DATA_INTEGRITY_EXCEPTION
        const errorMessage = get(error, 'data.message')

        if (!isDataIntegrityIssue) {
          handleError(error)
          return
        }

        if (errorMessage.includes('hh_contact')) {
          setFailureDialog({ isOpen: true, message: T.DELETE_CONTACT_ASSOCIATION_FAILURE })
        }

        if (errorMessage.includes('hh_call')) {
          setFailureDialog({ isOpen: true, message: T.DELETE_CONTACT_CALL_FOLLOWUP_FAILURE })
        }
      })
      .finally(() => {
        onClose()
        setIsOpenConfirmDeleteDialog(false)
      })
  }

  return (
    <>
      {(isPatchBeneficialOwnerLoading || isDeleteContactLoading) && <Loader />}
      <Menu
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { variant: 'outlined' },
        }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        disableScrollLock
      >
        <MenuItem onClick={onEdit}>{T.EDIT}</MenuItem>
        <MenuItem sx={switchMenuSx}>
          <HHSwitchIconField type="AccountStatus" label={T.ACTIVE_CONTACT} isChecked={isActive} onChange={handleContactActiveStatus} />
        </MenuItem>
        <MenuItem sx={switchMenuSx}>
          <HHSwitchIconField
            isChecked={isPropertyOwner}
            label={T.PROPERTY_OWNER}
            onChange={handlePropertyOwnerStatus}
            type="PropertyOwner"
          />
        </MenuItem>
        <MenuItem sx={switchMenuSx}>
          <HHSwitchIconField
            isChecked={isBeneficialOwnerOverride}
            label={T.BILLING_ADDRESS_OVERRIDE}
            onChange={handleBeneficialOwnerStatus}
            type="Invoice"
          />
        </MenuItem>
        <MenuItem sx={{ color: 'error.main' }} onClick={() => setIsOpenConfirmDeleteDialog(true)}>
          {T.DELETE_CONTACT}
        </MenuItem>
      </Menu>
      <HHConfirmDialog
        confirmDescription="Are you sure you want to delete this contact?"
        confirmTitle={T.DELETE_CONTACT}
        isOpen={isOpenConfirmDeleteDialog}
        onConfirm={handleDeleteConfirm}
        onClose={() => {
          onClose()
          setIsOpenConfirmDeleteDialog(false)
        }}
      />

      <HHConfirmDialog
        ConfirmButtonProps={{ color: 'primary' }}
        DialogProps={{ maxWidth: 'xs', fullWidth: true }}
        confirmButtonTitle={T.CLOSE}
        confirmDescription={failureDialog.message}
        confirmTitle={T.DELETE_CONTACT_FAILURE}
        isOpen={failureDialog.isOpen}
        onClose={() => setFailureDialog({ isOpen: false, message: '' })}
        onConfirm={() => setFailureDialog({ isOpen: false, message: '' })}
        showCancelButton={false}
      />
    </>
  )
}

BeneficialOwnersActionMenu.propTypes = {
  anchorEl: PropTypes.object,
  accountId: PropTypes.string,
  selectedContact: PropTypes.object,
  onEdit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default BeneficialOwnersActionMenu
